import type { Landmark } from '../../../../data/types';
import { difference, fromRing, union } from '../../../../geo/clip';
import type { Ring } from '../../../../geo/types';
import { planBounds, toLayout } from '../planFrame';
import { OUDE_KERK, oudeKerkFrame } from './oudeKerkPlan';

/** Retain the solid medieval church, excluding the reserved 1551–54 Lady Chapel.
 * The cut reaches beyond the modern trace: its approximately measured chapel
 * corners must not leave a thin invisible wall around ground that is unbuilt.
 * North-choir buttresses still occupy their actual 0.8 × 1.2 m masonry feet,
 * matching OudeKerkChoir's fitted buttresses in this part of the retained trace. */
export function oudeKerkObstacles(landmark: Landmark): readonly Ring[] {
  const frame = oudeKerkFrame(landmark);
  const bounds = planBounds(frame);
  const absent = OUDE_KERK.absent.mariakapel;
  const cut: Ring = [
    [absent.u0, absent.v0],
    [bounds.u1 + 1, absent.v0],
    [bounds.u1 + 1, bounds.v1 + 1],
    [absent.u0, bounds.v1 + 1],
  ];
  const retained = difference([fromRing(frame.ring)], [fromRing(cut)]);
  let u = OUDE_KERK.choir.bayU0;
  const buttresses = OUDE_KERK.choir.bays.slice(0, -1).map((bay) => {
    u += bay;
    const north = OUDE_KERK.choir.vN;
    return fromRing([
      [u - 0.4, north - 0.1],
      [u + 0.4, north - 0.1],
      [u + 0.4, north + 1.2],
      [u - 0.4, north + 1.2],
    ]);
  });
  return union([...retained, ...buttresses]).map((polygon) =>
    polygon.outer.map((point) => toLayout(frame, point))
  );
}
